import { useServicesQuery } from '@features/service/queries';
import { SubscriptionPlansEnum } from '@features/subscription/entity';
import { getDefaultPhonePrefix } from '@shared/components/phone-prefix-autocomplete';
import { useSubscriptionPlan } from '@shared/store';
import { Control, useForm } from 'react-hook-form';
import { EditClient } from '../../entity';
import { useCreateNewOrUpdateClientMutation } from '../../queries';
import { Identifiable } from '@shared/components/SelectField';
import { lastNameValidationRule, phoneValidationRule } from '@shared/entity';
import { IntlShape } from 'react-intl';
import { FormFieldConfig } from './client-form.types';
import Switch from '@shared/components/Switch/SwitchButton';
import { PhoneDuplicateValidator, usePhoneDuplicateValidator } from '@features/client/validators';

export const useClientForm = (
  enableAllowedServices: boolean,
  defaultValues?: EditClient,
  currentClientId?: string,
) => {
  const { mutate: createOrUpdateClientMutation } = useCreateNewOrUpdateClientMutation();
  const validatePhoneUnique = usePhoneDuplicateValidator(currentClientId);
  const methods = useForm<EditClient>({
    defaultValues: {
      phonePrefix: getDefaultPhonePrefix(),
    },
    values: defaultValues
      ? { ...defaultValues, phonePrefix: defaultValues.phonePrefix ?? getDefaultPhonePrefix() }
      : undefined,
  });
  const plan = useSubscriptionPlan();
  const isExpertPlan = plan === SubscriptionPlansEnum.EXPERT;

  const { data: services = [] } = useServicesQuery(enableAllowedServices);
  const serviceOptions = services.map(service => ({ id: service.id, name: service.serviceName }));

  const resetClientForm = () =>
    methods.reset({
      firstName: undefined,
      lastName: undefined,
      note: undefined,
      phone: undefined,
      phonePrefix: undefined,
    });

  return {
    methods,
    resetClientForm,
    createOrUpdateClientMutation,
    isExpertPlan,
    isRegularClient: methods.watch('regularClient'),
    serviceOptions: enableAllowedServices ? serviceOptions : [],
    validatePhoneUnique,
  };
};

export const getClientFormFields = (
  intl: IntlShape,
  serviceOptions: Identifiable[] | undefined,
  isExpertPlan: boolean,
  isRegularClient: boolean,
  control: Control<EditClient>,
  validatePhoneUnique?: PhoneDuplicateValidator,
): FormFieldConfig[] => [
  {
    type: 'text',
    fieldPath: 'firstName',
    label: intl.formatMessage({ defaultMessage: 'Jméno', id: 'client.firstName' }),
    required: false,
    shouldDisplay: true,

    helperText: intl.formatMessage({
      defaultMessage: 'V případě online rezervací uvidí klient své jméno.',
      id: 'client.firstName.helperText',
    }),
  },
  {
    type: 'text',
    fieldPath: 'lastName',
    label: intl.formatMessage({ defaultMessage: 'Příjmení', id: 'client.lastName' }),
    required: true,
    rules: lastNameValidationRule,
    shouldDisplay: true,
    helperText: intl.formatMessage({
      defaultMessage: 'V případě online rezervací uvidí klient své příjmení.',
      id: 'client.lastName.helperText',
    }),
  },
  {
    type: 'phonePrefix',
    fieldPath: 'phonePrefix',
    label: '',
    shouldDisplay: true,
  },
  {
    type: 'tel',
    fieldPath: 'phone',
    label: intl.formatMessage({ defaultMessage: 'Telefon', id: 'client.clientInfo' }),
    required: true,
    rules: {
      ...phoneValidationRule,
      validate: validatePhoneUnique
        ? {
            phoneFormat: phoneValidationRule.validate,
            phoneUnique: validatePhoneUnique,
          }
        : phoneValidationRule.validate,
    },
    shouldDisplay: true,
  },
  {
    type: 'text',
    fieldPath: 'note',
    label: intl.formatMessage({ defaultMessage: 'Informace o klientovi', id: 'client.note' }),
    multiline: true,
    minRows: 2,
    maxRows: 10,
    shouldDisplay: true,
  },
  {
    type: 'select',
    fieldPath: 'allowedServices',
    label: intl.formatMessage({
      defaultMessage: 'Povolené služby',
      id: 'client.allowedServices',
    }),
    items: serviceOptions,
    multiple: true,
    keyExtractor: service => service.id,
    labelExtractor: service => service.name,
    shouldDisplay: !!(isExpertPlan && serviceOptions && serviceOptions.length > 0),
  },
  {
    type: 'captionCard',
    fieldPath: 'regularClient',
    label: '',
    captionTitle: intl.formatMessage({
      id: 'addVisitForm.regularClient',
      defaultMessage: 'Stálý klient',
    }),
    caption:
      'Slouží pro přehledy kalkulačky, můžete tak sledovat kolik procent vašich klientů se vrací.',
    endElement: (
      <Switch
        fieldPath="regularClient"
        control={control} // Control will be passed from parent
        slotProps={{
          track: {
            sx: { backgroundColor: isRegularClient ? 'success.main' : 'secondary.main' },
          },
          thumb: {
            sx: { backgroundColor: isRegularClient ? 'success.main' : 'secondary.main' },
          },
        }}
      />
    ),
    shouldDisplay: true,
  },
];
